import { Icon } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import * as React from 'react';
import Stardog from 'stardog';

import { ExternalLink } from 'src/ui/components/ExternalLink';

export const copy = {
  appNavBar: {
    title: 'Stardog Cloud',
  },
  auth0: {
    label: 'Sign in with Auth0',
  },
  azure: {
    label: 'Sign in with Microsoft',
  },
  diagnostic: {
    title: 'Diagnostic Report',
    support: 'Please share the following details with support:',
    copy: 'Copy To Clipboard',
    authHeader: 'Test authorization header received',
    customHeader: 'Test custom header received',
    copiedNotification: 'Diagnostic information copied to clipboard.',
    notConnected: 'Not Connected',
    subject: 'OAuth Subject',
    username: 'Username',
    provider: 'Provider',
    connectionValid: 'Your connection to Stardog is valid!',
    connectionInvalid:
      'Your connection to Stardog is not valid, please ask an administrator to verify your username has access.',
    verifiedUser: 'Email Verified',
    voicebox: {
      title: 'Voicebox Report',
      severityTitle: {
        error: 'Error',
        warning: 'Warning',
        info: 'Info',
      },
      generateReportButton: 'Generate Report',
      generateReportResponseError: /* istanbul ignore next  */ (
        error: string
      ) => `Error fetching Voicebox Diagnostics: ${error}`,
      selector: {
        database: 'Select Database',
        model: 'Select Model',
      },
    },
  },
  errors: {
    browserAuthFailed: {
      before:
        'Could not connect to your endpoint using browser authentication. Please check your browser configuration.',
      link: 'Click here',
      after: 'to check diagnostic page',
    },
    invalidUserPassword: 'Invalid username and/or password.',
    verificationFailed:
      'Verify your endpoint address is correct, or enter username password for this connection',
    notFound: 'Stardog was not found. Talk to your admin.',
    unknown: 'An unknown error occurred. Talk to your admin.',
    connectionFailed:
      'Unable to contact Stardog server, please check your connection.',
    degraded: {
      before: 'This connection will not have an optimal experience,',
      link: 'click here',
      after: 'for an example configuration',
    },
    unverified: {
      before: 'You need to',
      link: 'verify your email address',
      after: 'to make a purchase.',
    },
    unverifiedSent: 'We resent you an email to verify your account',
    unverifiedAlreadySent:
      'We have already sent you an email to verify your account',
    unauthorized: {
      before: 'There is an error with your connection, please',
      link: 'click here',
      after: 'to check diagnostic page',
    },
    ssoTokenRequired:
      'Organization SSO sign-in is required for this connection.',
    ssoTokenExchangeFailed:
      "SSO token exchange failed for this connection. Your organization's identity provider may be misconfigured.",
  },
  google: {
    label: 'Sign in with Google',
  },
  okta: {
    label: 'Sign in with Okta',
  },
  ping: {
    label: 'Sign in with Ping',
  },
  duo: {
    label: 'Sign in with Duo',
  },
  dashboard: {
    title: 'Please choose an application:',
    openAction: 'Open',
    explorer: {
      label: 'Explorer',
      description:
        'An intuitive interface to visually browse and query your Knowledge Graph.',
    },
    studio: {
      label: 'Studio',
      description: 'A complete IDE to program your Knowledge Graph.',
    },
    designer: {
      label: 'Designer',
      description:
        'A no-code, visual environment to model, map, and publish data for your Knowledge Graph.',
    },
  },
  login: {
    username: 'Username',
    password: 'Password',
    label: 'Connect',
    retry: 'Retry',
  },
  openid: {
    label: 'Sign in with SSO', // TODO: use config setting
  },
};

export const components = {
  defaults: {
    confirmation: {
      title: 'Are you sure?',
      no: 'No',
      yes: 'Yes',
    },
  },
  notFound: {
    title: 'Page Not Found',
    description: 'Sorry we are unable to find a matching page.',
  },
  shareNotFound: {
    title: 'Link Not Found',
    description:
      'Sorry the link you have requested is invalid or expired. Make sure you have the correct URL.',
  },
  shareOrgConnectionUnavailable: {
    title: 'Endpoint Unavailable',
    description:
      'This endpoint is not available. You may not have access to this organization, or the endpoint may have been removed.',
  },
  invitationNotFound: {
    title: 'Invitation Not Found',
    description:
      'Sorry the invitation you have requested is invalid or expired. Please ask the sender to send a new invitation.',
  },
  invitationDifferentUser: {
    title: 'Invitation for Different User',
    info: (expected: string, actual = 'unknown') =>
      `This invitation is for ${expected}, but you are logged in as ${actual}.`,
    instruction: (expected: string) =>
      `Please log out and log in as ${expected} to accept this invitation.`,
    logoutButton: 'Log Out',
  },
  invitation: {
    endpoint: {
      name: 'Endpoint Name',
      info: (sender: string, endpoint: string) => (
        <>
          <b>{sender}</b>{' '}
          <span>is inviting you to join the Knowledge Graph endpoint at</span>{' '}
          <b>{endpoint}</b>
        </>
      ),
      validationExists: 'An Endpoint with this name already exists',
      validationRequired: 'Endpoint name is required',
    },
    organization: {
      info: (sender: string, organization: string) => (
        <>
          <b>{sender}</b> <span>is inviting you to join the organization</span>{' '}
          <b>{organization}</b>
        </>
      ),
    },
    notifications: {
      connectionFailure: (err: string) => `Could not connect: ${err}`,
      acceptFailure: (err: string) =>
        `Problem accepting invitation: ${err || 'Unknown Error'}`,
    },
    title: 'Invitation',
    acceptButton: 'Accept Invitation',
    loginButton: 'Log In',
    signupButton: 'Sign Up',
    cancelButton: 'Cancel',
  },
  shareConnection: {
    title: 'Share Knowledge Graph endpoint',
    description:
      'Share this Knowledge Graph endpoint with others. This will also set up any accounts that need to be created.',
    usersLabel: 'Users',
    userPlaceholder: 'Email',
    addUserButton: 'Add User',
    removeUserTooltip: 'Remove User',
    accessLevelLabel: 'Access Level',
    databaseLabel: 'Database',
    inviteButton: 'Share Endpoint',
    closeButton: 'Close',
  },
  connectionDialog: {
    form: {
      advanced: 'Advanced Options',
      username: 'Username',
      password: 'Password',
      endpoint: 'Endpoint URL',
      endpointHelp: '(URL of your existing Knowledge Graph endpoint)',
      sso: 'Connect using SSO credentials:',
      ssoHelp: (username: string | undefined) =>
        username ? `Will log in as: ${username}` : 'Loading...',
      name: 'Name',
      delete: 'Enter Name to Delete',
      nameHelp: '(A unique name to identify this Knowledge Graph endpoint)',
      internalEndpoint: 'Internal Endpoint',
      internalEndpointHelp:
        'Optional internal endpoint for server-to-server communication in private networks',
    },
    formValidation: {
      nameRequired: 'Name is required',
      nameUniq: 'A Knowledge Graph endpoint with this name already exists',
      endpointRequired: 'Endpoint is required',
      passwordRequired: 'Password is required when providing a username ',
      usernameRequired: 'Username is required when providing a password',
      invalidEndpoint: 'Endpoint must be a valid url',
      deleteNameMatch:
        'You must enter the name exactly to delete Knowledge Graph endpoint',
    },
    new: {
      submit: 'Connect',
    },
    delete: {
      title: 'Are you sure you want to delete',
      submit: 'Delete',
      cancel: 'Cancel',
    },
    select: {
      title: 'Select a Knowledge Graph endpoint',
      submit: 'Select',
    },
  },
  deleteAccountDialog: {
    title: 'Stardog Cloud Account Deletion',
    callout: (
      <p>
        Account deletion <b>cannot</b> be undone. This will permanently delete
        your account information and any Stardog Cloud instances for which you
        are the owner of. All active subscriptions will be cancelled.
      </p>
    ),
    deletionConfirmationLabel: "Please type 'DELETE' to confirm",
    deletionButton: 'I understand the consequences, delete my account',
  },
  verifyEmailDialog: {
    body: {
      title: 'Verify your email address',
      currentIdentity: (email: string) => (
        <p>
          You&apos;ve entered <b>{email}</b> as the email address for your
          account.
        </p>
      ),
      pleaseConfirm:
        'Please confirm your identity to get a Stardog Cloud instance.',
    },
    emailButtons: [
      {
        name: 'Gmail',
        url:
          'https://mail.google.com/mail/u/0/#search/from%3A(support%40stardog.com)+subject%3A(Verify+your+email)+in%3Aanywhere',
      },
      { name: 'Outlook', url: 'https://outlook.live.com/mail/0/' },
      {
        name: 'Yahoo',
        url:
          'https://mail.yahoo.com/d/search/keyword=from%253Asupport%2540stardog.com%2520subject%253A%2522Verify%2520your%2520email%2522',
      },
      {
        name: 'Proton',
        url:
          'https://mail.proton.me/u/0/all-mail#keyword=Verify+your+email&from=support%40stardog.com',
      },
      { name: 'iCloud', url: 'https://www.icloud.com/mail/' },
    ],
    resendButton: {
      text: {
        before: "Didn't get the email?",
        link: 'Send it again.',
      },
    },
    contactUs: {
      text: {
        before: 'Having trouble?',
        link: 'Contact us.',
      },
      url: 'https://www.stardog.com/company/contact/',
    },
  },
  verifyChallenge: {
    title: 'Verify your email',
    subtitle: (email: string, organization: string) => (
      <>
        Enter the 8-digit code sent to <strong>{email}</strong> to complete
        sign-in to <strong>{organization}</strong>.
      </>
    ),
    codePlaceholder: '8-digit code',
    submitButton: 'Verify',
    resendButton: 'Resend code',
    backButton: 'Back to login',
    resendSuccess: 'A new code has been sent to your email.',
    defaultVerifyError: 'Verification failed. Please try again.',
    defaultResendError: 'Failed to resend code.',
    unexpectedError: 'An unexpected error occurred. Please try again.',
    unexpectedResendError: 'An unexpected error occurred.',
  },
  loginPage: {
    logoAlt: 'Stardog',
    main: {
      title: 'Sign in to your account',
      auth0Button: 'Continue with Stardog Cloud',
      divider: 'or',
      ssoLabel: 'Organization SSO',
      ssoPlaceholder: 'Enter organization domain',
      ssoSubmit: 'Continue',
      ssoSubmitLoading: 'Looking up...',
      footerPrompt: "Don't have an account?",
      footerLink: 'Get Started',
    },
    org: {
      title: (orgName: string) => (
        <>
          Sign in to <strong>{orgName}</strong>
        </>
      ),
      providerButton: (providerLabel: string) =>
        `Continue with ${providerLabel}`,
      back: 'Back',
    },
    errors: {
      orgLookupFailed: 'Failed to look up organization',
    },
    providers: {
      microsoft: 'Microsoft Entra ID',
      okta: 'Okta',
    },
  },
  verifyEmailPage: {
    title: 'Verify your email',
    description: (email: string) => (
      <p>
        We sent a verification email to <strong>{email}</strong>. Please check
        your inbox and click the link to continue.
      </p>
    ),
    resendPrompt: "Didn't get the email?",
    resendLink: 'Send it again',
    contactPrompt: 'Having trouble?',
    contactLink: 'Contact us',
    contactUrl: 'https://www.stardog.com/company/contact/',
    resendSuccess: 'Verification email sent!',
    resendError: 'Failed to send email. Please try again later.',
    backToLogin: 'Back to login',
  },
  unsupportedBrowserDialog: {
    title: 'Unsupported Browser',
    body:
      "You're using a browser we don't support. Try the latest version of Firefox, Google Chrome, or Microsoft Edge to have an optimal experience.",
    acknowledgeButtonText: 'Acknowledge',
  },
  welcomeToEssentialsDialog: {
    title: 'Welcome to Stardog Essentials',
    body: (
      <>
        <p>
          Your Knowledge Graph endpoint was successfully upgraded to Stardog
          Essentials
        </p>
        <p>
          Please note that certain modifications may take up to 48 hours for
          completion, so you may experience a temporary downtime during this
          period
        </p>
      </>
    ),
    buttonText: 'I understand',
  },
  dataSourceDialog: {
    title: 'Add Data Source',
    form: {
      name: 'Name',
      nameHelp: '(A unique name for this data source)',
      uniqueKeySets: 'Unique Key Sets',
      rdbms: {
        url: 'URL',
        username: 'Username',
        password: 'Password',
        driver: 'Driver',
        dialect: 'Dialect',
        catalog: 'Catalog',
        catalogs: 'Catalogs',
        schema: 'Schema',
        schemas: 'Schemas',
      },
      mongodb: {
        uri: 'URI',
        uriHelp: '(The URI of your MongoDB Server including credentials)',
      },
      elasticsearch: {
        urls: 'URLs',
        indexes: 'Indexes',
        username: 'Username',
        password: 'Password',
      },
      cassandra: {
        point: 'Contact Point',
        port: 'Port',
        keyspace: 'Keyspace',
        username: 'Username',
        password: 'Password',
        filtering: 'Filtering',
      },
      sparql: {
        url: 'URL',
        username: 'Username',
        password: 'Password',
        graph: 'Graph',
        statsBasedOptimization: 'Stats Based Optimization',
      },
    },
    new: {
      submit: 'Test and Save',
    },
    alert: {
      created: (name: string) => `Created datasource ${name}`,
      failed: (name: string, response: Stardog.HTTP.Body) =>
        `Failed to create datasource ${name} - ${response.body.code}: ${response.body.message}`,
    },
    navigation: {
      next: 'Next',
      finish: 'Finish',
    },
  },
  connectionList: {
    getCloud: 'Create new Knowledge Graph Endpoint',
  },
  moveConnectionToOrgDialog: {
    menu: 'Move to org',
    title: 'Move connection to organization',
    cancel: 'Cancel',
    checkOrg: 'Check org',
    submit: 'Move',
    ineligible:
      'This connection is not in a personal organization and cannot be moved.',
    ineligibleDatabricks:
      'This connection is linked to a Databricks partner connection and cannot be moved.',
    invalidOrg: 'Not a valid public organization.',
    targetOrgRequired: 'Target organization is required',
    addMe: 'Add me to the organization (as an active member)',
    youAreMember: "you're already a member",
    keepSource: 'Keep the existing connection(s) (gradual swap-over)',
    keepSourceHelp:
      'Create the org connection and its access but leave the existing personal ' +
      "connections in place so users don't lose access. Clean up the leftovers " +
      'later with "Clean up org connection".',
    keepSourceWarning:
      'The existing personal connection(s) will be deleted. Users not migrated ' +
      'below will lose access.',
    multipleConnections: {
      title: 'This cloud has more than one connection',
      body: (labels: string) =>
        'A cloud should only have one connection. Moving detaches every ' +
        'connection except the new org one, so investigate these before ' +
        `moving: ${labels}`,
    },
    form: {
      targetOrg: 'Target organization domain',
      targetOrgHelp:
        'The connection will be moved into this public organization. ' +
        'Run "Check org" to verify it and see who is already a member.',
      visibility: 'Visibility',
      orgWide: 'Org-wide — all members can use this connection',
      restricted: 'Restricted — only migrated users can use this connection',
      access: 'Access to migrate',
      accessEmpty: 'This connection has no access entries.',
      accepted: 'accepted',
      pending: 'pending',
      owner: 'owner',
      ownConnection: 'own connection',
      alreadyMember: 'already a member',
      roleMember: 'Member',
      roleAdmin: 'Admin',
      members: 'Current organization members',
      noMembers: 'This organization has no members yet.',
    },
  },
  cleanupOrgConnectionDialog: {
    menu: 'Clean up org connection',
    title: 'Clean up leftover connections',
    cancel: 'Cancel',
    submit: 'Apply cleanup',
    notMoved:
      'This cloud does not have an organization connection yet. Move it into an ' +
      'organization first.',
    empty: 'No leftover personal connections point at this endpoint.',
    endpoint: (endpoint: string) => `Endpoint: ${endpoint}`,
    org: (name: string) => `Organization: ${name}`,
    orphanedHeading: 'Leftover personal connections',
    membersHeading: 'Org members who cannot reach this connection yet',
    membersNone: 'All organization members can reach this connection.',
    actionRemove: 'Remove connection',
    actionMigrate: 'Migrate to org',
    actionLeave: 'Leave unchanged',
    roleMember: 'Member',
    roleAdmin: 'Admin',
    hasOrgAccess: 'already has org access',
    isMember: 'org member',
  },
  transferCloudToUserDialog: {
    menu: 'Transfer to user',
    title: 'Transfer cloud to user',
    cancel: 'Cancel',
    checkUser: 'Check user',
    submit: 'Transfer',
    ineligible:
      'This connection is not in a personal organization and cannot be transferred.',
    ineligibleDatabricks:
      'This connection is linked to a Databricks partner connection and cannot be transferred.',
    targetUser: 'Target user email',
    targetUserHelp:
      'The cloud will be transferred to this user. Enter their email to load ' +
      'their eligible connections.',
    targetUserRequired: 'Target user email is required',
    userNotFound: 'No user found with that email.',
    destination: 'Destination connection',
    destinationHelp: 'Choose which of the user’s connections to transfer onto.',
    destinationNew:
      'This user has no connection to this endpoint — a new one will be created for them.',
    destinationChoose: 'Select a destination connection to continue.',
    transferOwner: 'Also make the target user the cloud owner',
    transferOwnerHelp:
      'Reassigns the cloud’s owner to the target user so it is no longer flagged ' +
      'as owned by someone else. Leave unchecked to keep the current owner.',
    carriedHeading: 'Access to carry over',
    carriedEmpty: 'This connection has no access to carry over.',
    actionCarry: 'Carry over',
    actionDrop: 'Drop',
    otherHeading: 'Left as-is (not moved)',
    otherHelp:
      'Other connections and access pointed at this endpoint. The transfer only ' +
      'moves the source connection’s own access; these are shown for context.',
    otherConnections: (labels: string) => `Other connections: ${labels}`,
    accepted: 'accepted',
    pending: 'pending',
  },
  manageApiTokens: {
    apiTokenRow: {
      delete: {
        cancel: 'Cancel',
        failed:
          'The token could not be deleted. Reload the page to see its current state.',
        submit: 'Delete',
        user: {
          message: (name: string) =>
            `Delete the token "${name}"? Anything using it stops working immediately. This cannot be undone.`,
          title: 'Delete token',
        },
        voicebox: {
          message: (name: string) =>
            `Delete the API key "${name}"? Anything using it stops working immediately. This cannot be undone.`,
          title: 'Delete API key',
        },
      },
      expired: 'Expired',
      table: {
        name: 'Name',
        created: 'Created',
        lastUsed: 'Last Used',
        expires: 'Expires',
      },
    },
    createApiTokenDialog: {
      cancel: 'Cancel',
      close: 'Close',
      submit: 'Create',
      title: {
        user: 'Create User API Token',
        voicebox: 'Create Voicebox API Key',
      },
      form: {
        expiresDays: 'Expires In (days)',
        expiresDaysHelp: (max: number) => `Between 1 and ${max} days.`,
        expiresRange: (max: number) =>
          `Expiry must be a whole number between 1 and ${max} days`,
        expiresRequired: 'Expiry is required',
        token: {
          user: 'Token',
          voicebox: 'API Key',
        },
        tokenHelp: {
          user:
            'Copy your token now. It is shown only once and cannot be retrieved later.',
          voicebox:
            "Be sure to copy your API Key now. For security purposes, you won't see it again.",
        },
        name: 'Name',
        nameRequired: 'Name is required',
      },
    },
    userTokens: {
      add: 'New Token',
      creationDisabled:
        'New tokens cannot be created because user API tokens are turned off for this deployment. Existing tokens can still be deleted here.',
      description:
        'Tokens authenticate API requests as the current user. A token is shown once, when it is created.',
      empty: 'You have no API tokens yet.',
      error: {
        message: 'Your API tokens could not be loaded.',
        retry: 'Retry',
        tokensStillWork:
          'Existing tokens keep working. Only this list is affected.',
      },
      header: 'User API Tokens',
    },
    voiceboxAppCard: {
      add: 'New App Key',
      delete: {
        cancel: 'Cancel',
        confirmationMessage:
          'Are you sure you want to delete this Voicebox App?',
        menu: 'Delete App',
        submit: 'Delete',
        title: 'Delete Voicebox App',
      },
      empty: 'No API keys.',
      update: {
        cancel: 'Cancel',
        menu: 'Rename',
        submit: 'Rename',
        title: 'Rename Voicebox App',
      },
      tokens: 'API Keys',
    },
    voiceboxApps: {
      add: 'New Voicebox App',
      createDialog: {
        cancel: 'Cancel',
        submit: 'Create',
        title: 'Create Voicebox App',
      },
      updateDialog: {
        cancel: 'Cancel',
        submit: 'Rename',
        title: 'Rename Voicebox App',
      },
      empty: 'No Voicebox Apps.',
      header: 'Manage Voicebox Apps',
    },
    voiceboxConfig: {
      connection: {
        label: 'Endpoint',
        required: 'Endpoint is required',
        empty: {
          before: 'You do not have any saved Knowledge Graph endpoints.',
          link: 'Get Stardog Essentials',
          after: ' to get started with Voicebox.',
        },
      },
      database: {
        label: 'Database',
        placeholder: 'Select Database',
        required: 'Database is required',
      },
      model: {
        default: 'Default',
        label: 'Model',
      },
      name: {
        label: 'Name',
        required: 'Name is required',
      },
      namedGraphs: {
        dividers: {
          alias: 'Aliases',
          named: 'Named',
          virtual: 'Virtual',
        },
        label: 'Graph',
        selected: (num: number) => `${num} selected`,
      },
      reasoning: {
        label: 'Reasoning',
        enabled: 'Yes',
        disabled: 'No',
      },
    },
  },
  navbar: {
    aside: {
      portalEndpoint: {
        manageEndpoints: 'Manage Endpoints',
        selector: 'Endpoint',
      },
    },
    avatar: {
      menuItems: {
        deleteAccount: 'Delete Account',
        employeeHome: 'Employee Home',
        logout: 'Logout',
        manageApiTokens: 'Manage API Keys',
        manageBilling: 'Manage Billing',
        personalOrganization: 'My Workspace',
        addOrganization: 'Add Organization',
        updateCookieConsent: 'Update Cookie Consent',
        updateProfile: 'Update Profile',
      },
    },
    landing: {
      endpoint: 'Endpoint',
    },
    organization: {
      dashboard: 'Organization Dashboard',
    },
    sduDashboard: 'Stardog Units',
    voicebox: {
      addNew: 'Add New',
      chatList: 'Chat list',
      chatListItems: 'LATEST',
      settings: 'Settings',
    },
  },
  sduDashboard: {
    title: 'Stardog Units',
    totalTileLabel: 'Stardog Units (Utilized)',
    endpointsTileLabel: 'Connected Endpoints',
    dateRangeLabel: 'Select a Date Range to view data',
    dateRangePlaceholder: 'Start Date - End Date',
    chart: {
      titleOverview: 'Stardog Units Consumption',
      titleEndpoint: 'Units Consumption Over Time',
      xAxis: 'Time Period',
      yAxis: 'Stardog Units',
      groupBy: 'Group by',
      timeGrain: {
        day: 'Day',
        week: 'Week',
        month: 'Month',
      },
    },
    breakdownTitle: 'Consumption Breakdown',
    endpointTableTitle: 'Endpoint Consumption',
    endpointTableSearchPlaceholder: 'Search',
    endpointTableColumns: {
      url: 'URL',
      units: 'Stardog Units',
    },
    groupLabels: {
      dataModelingAndManagement: 'Data Modeling & Management',
      naturalLanguageInteractions: 'Natural Language Interactions',
      queryAndGraphExploration: 'Query & Graph Exploration',
      documentProcessing: 'Document Processing',
      programmaticAccess: 'Programmatic Access',
    },
    empty: 'No data available for the selected range.',
    loading: 'Loading…',
    noData: '—',
  },
  voiceboxResponseReport: {
    title: 'Voicebox Responses Report',
    description: 'Report will be downloaded',
    startDate: 'Start Date',
    endDate: 'End Date',
    dateRangePlaceholder: 'Start Date - End Date',
    cancel: 'Cancel',
    submit: 'Download Report',
    success: 'Report download started. This may take a few minutes.',
    missingDates: 'Start date and end date are required.',
    genericError: 'Unable to download Voicebox Responses Report.',
  },
  stardogUnitUsage: {
    csvHeaders: [
      'Date',
      'Endpoint Name',
      'Endpoint URL',
      'Daily SDU Total',
      'Remarks',
    ],
    downloadAttempting: (name: string) =>
      `Attempting to download usage data for "${name}", this may take a few minutes.`,
    downloadNoData: (name: string) =>
      `No usage data is available yet for "${name}". No report was downloaded.`,
    downloadSuccess: (name: string) =>
      `Successfully downloaded Stardog Unit Usage for "${name}".`,
    downloadFailed: 'Failed to download Stardog Unit Usage for',
  },
  createProfileDialog: {
    title: 'Create Stardog Profile',
    textInputs: {
      firstName: {
        label: 'First name*',
      },
      lastName: {
        label: 'Last name*',
      },
      companyName: {
        label: 'Company name*',
      },
      useCase: {
        label: 'Company use case*',
      },
    },
    selectors: {
      industry: {
        label: 'Company industry*',
        options: {
          financialServices: 'Financial Services',
          lifeSciences: 'Life Sciences',
          manufacturing: 'Manufacturing',
          other: 'Other',
        },
      },
      bestDescribesCompany: {
        label: 'Select the option that best describes your company*',
        options: {
          consultingServices: 'Consulting or services firm',
          enterprise: 'Enterprise company',
          smallMedium: 'Small or medium business',
          govNonProfit: 'Government or non-profit',
          personal: 'Personal',
        },
      },
      bestDescribesRole: {
        label: 'What role best describes you?*',
        options: {
          developer: 'Data engineer, scientist, or developer',
          ontologist: 'Data ontologist, librarian, or architect',
          admin: 'System administrator or operator',
          analyst: 'Analyst or business expert',
          none: 'None of the above',
        },
      },
    },
    checkboxes: {
      agree: 'I agree to the Stardog',
      usageTerms: 'Terms of Use',
      privacyPolicy: 'Privacy Policy',
    },
    buttons: {
      back: 'Back',
      submit: 'Access Stardog',
    },
    formValidation: {
      companyRequired: 'Company name is required',
      firstNameRequired: 'First name is required',
      lastNameRequired: 'Last name is required',
      useCaseRequired: 'Company use case is required',
      industryRequired: 'Company industry is required',
      bestDescribesCompany:
        'You must select an option that best describes your company',
      bestDescribesRole:
        'You must select an option that best describes your role',
      privacyRequired: 'You must agree to the Stardog Privacy Policy',
      termsRequired: 'You must agree to the Stardog Terms of Use',
    },
  },
  updateProfileDialog: {
    title: 'Update Stardog Profile',
    submit: 'Update',
  },
  getStarted: {
    title: 'Get Started with Stardog Cloud',
    overview:
      'Industry recognized Semantic AI Platform provided as a managed service.',
    pricingUnavailable:
      'Temporarily unavailable. Please try again later or contact us',
    hostingDisclaimer: {
      ap: 'All endpoints are currently hosted in the AP (Sydney).',
      eu: 'All endpoints are currently hosted in the EU (Frankfurt).',
      us: 'All endpoints are currently hosted in the US.',
    },
    blockedEmail: {
      message: (
        <>
          A business email address is required to request a free Stardog Cloud
          endpoint. Free or educational email domains (such as Gmail, Yahoo, or
          .edu) are not eligible for this feature.
          <br />
          <br />
          Please sign up using a business email address.
        </>
      ),
      yesPrompt: 'Sign Up',
      noPrompt: 'Cancel',
    },
    comparisonTitles: {
      pricing: 'Pricing',
      hosting: 'Hosting',
      storedEdges: 'Stored edges',
      databases: 'Databases',
      voiceboxDbCount: 'Voicebox Databases',
      security: 'Authentication',
      backups: 'Backups/Retention',
      privateLink: 'AWS Private Link',
      biSqlEndpoint: 'BI/SQL Endpoint',
      availability: 'Service Availability',
      support: 'Support',
      profServices: 'Professional Services',
      selfManageOption: 'Option to self-manage Stardog',
    },
    editions: {
      free: {
        tag: 'Learning environment to create your first Knowledge Graph.',
        title: 'Free',
        cta: 'Get Stardog Free',
        ctaDisabled: 'Coming Soon',
        ctaDisabledTooltip: {
          ap:
            'Stardog Free is not currently available for use in our AP-hosted Stardog Cloud experience. Please use our US-hosted Stardog Cloud for Free endpoints: https://cloud.stardog.com.',
          eu:
            'Stardog Free is not currently available for use in our EU-hosted Stardog Cloud experience. Please use our US-hosted Stardog Cloud for Free endpoints: https://cloud.stardog.com.',
        },
        priceText: 'Free',
        description:
          "Stardog Free is the industry's first cloud-native Enterprise Knowledge Graph Platform that connects data in every cloud as well as on-premise environments. Stardog transforms your existing enterprise data infrastructure into an end-to-end, comprehensive data fabric and answers complex queries across data silos, unifies data across the enterprise ecosystem based on its meaning, and context to create a true, connected network of knowledge.",
        features: {
          title: 'Feature Highlights',
          list: [
            'Shared Cloud hosting',
            'Store up to 1M edges',
            'Community Support',
          ],
        },
        specs: {
          pricing: 'Free',
          hosting: 'Shared',
          storedEdges: (
            <>
              Up to <b>1 million</b>
            </>
          ),
          databases: '3',
          voiceboxDbCount: '3',
          security: 'SSO/Google',
          backups: '',
          availability: '',
          support: (
            <a href="https://community.stardog.com/">
              Community <Icon icon={IconNames.ARROW_RIGHT} />
            </a>
          ),
          privateLink: false,
          biSqlEndpoint: false,
          profServices: false,
          selfManageOption: false,
        },
      },
      essentials: {
        tag: 'Everything you need for a PoC or development environment.',
        title: 'Essentials',
        cta: 'Get Stardog Essentials',
        ctaDisabled: 'Contact Us',
        priceText: '$39/user per month',
        description:
          "Stardog Essentials is the industry's first cloud-native Enterprise Knowledge Graph Platform that connects data in every cloud as well as on-premise environments. Stardog transforms your existing enterprise data infrastructure into an end-to-end, comprehensive data fabric and answers complex queries across data silos, unifies data across the enterprise ecosystem based on its meaning, and context to create a true, connected network of knowledge.",
        features: {
          title: 'Everything in Free, plus',
          list: [
            'Stardog Voicebox',
            'Includes 5 users',
            'Dedicated Cloud hosting',
            'Store up to 10M edges',
            '95% uptime SLA',
          ],
        },
        specs: {
          pricing: '$39/user per month (5 users)',
          hosting: 'Dedicated',
          storedEdges: (
            <>
              Up to <b>10 million</b>
            </>
          ),
          databases: 'Unlimited',
          voiceboxDbCount: '1',
          security: 'SSO/Google',
          backups: '',
          availability: '95% uptime',
          support: (
            <a href="https://community.stardog.com/">
              Community <Icon icon={IconNames.ARROW_RIGHT} />
            </a>
          ),
          privateLink: false,
          biSqlEndpoint: false,
          profServices: false,
          selfManageOption: false,
        },
      },

      enterprise: {
        tag:
          'Full production environment tailored to the needs of your company.',
        cta: 'Contact Us',
        title: 'Enterprise',
        priceText: 'Custom',
        description:
          "Stardog Enterprise is the industry's first cloud-native Enterprise Knowledge Graph Platform that connects data in every cloud as well as on-premise environments. Stardog transforms your existing enterprise data infrastructure into an end-to-end, comprehensive data fabric and answers complex queries across data silos, unifies data across the enterprise ecosystem based on its meaning, and context to create a true, connected network of knowledge.",
        features: {
          title: 'Feature Highlights',
          list: [
            'Option to self-manage Stardog',
            'Custom limit for stored edges',
            // 'Daily backups, 30d retention',
            'Premium Support, 99.9% uptime SLA',
          ],
        },
        specs: {
          pricing: (
            <a href="https://www.stardog.com/company/contact/">
              Contact us <Icon icon={IconNames.ARROW_RIGHT} />
            </a>
          ),
          hosting: 'Dedicated',
          storedEdges: 'Custom',
          databases: 'Unlimited',
          voiceboxDbCount: 'Custom',
          security: 'SSO/Google, Azure AD, OpenID',
          backups: 'Daily/30 days',
          availability: '99.9% uptime',
          support: (
            <a href="https://www.stardog.com/support/">
              Premium <Icon icon={IconNames.ARROW_RIGHT} />
            </a>
          ),
          privateLink: true,
          biSqlEndpoint: true,
          profServices: true,
          selfManageOption: true,
        },
      },
    },
    form: {
      agree: 'I agree to the Stardog',
      termsOfService: 'Terms of Service',
    },
    formValidation: {
      termsRequired: 'You must agree to the Stardog Terms of Service',
    },
    contactUsUrl: 'https://www.stardog.com/company/contact/',
    productNotFound: 'Error adding to cart, product missing',
  },
  ServerOverview: {
    button: {
      contactOwner: 'Contact Owner',
      manageBilling: 'Manage Billing',
      upgradePlan: 'Upgrade Plan',
    },
    title: {
      metrics: 'Metrics',
      stardogEssentials: 'Stardog Essentials',
      stardogFree: 'Stardog Free',
    },
    labels: {
      storedEdges: 'Stored Edges',
    },
    metrics: {
      queries: {
        label: 'Queries',
        description:
          'The total number of queries against all of your knowledge graphs.',
      },
      edges: {
        label: 'Edges',
        description:
          'The total number of relationships (edges) in all of your knowledge graphs.',
      },
      nodes: {
        label: 'Nodes',
        description:
          'The total number of connected nodes (nodes with outgoing edges) in all of your knowledge graphs.',
      },
      trialDaysRemaining: {
        label: 'Trial Days Remaining',
        description: 'Number of days remaining in your trial period.',
        days: (num: number) => `${num} day${num === 1 ? '' : 's'}`,
      },
      hostingEssentials: {
        label: 'Hosting',
        description:
          'Dedicated server resources supporting up to 10 million edges.',
        value: 'Dedicated',
      },
      hostingFree: {
        label: 'Hosting',
        description:
          'Shared server resources, which might impact query performance. Your data is always isolated from other environments.',
        value: 'Shared',
      },
    },
  },
  contactOwnerDialog: {
    title: 'Contact owner',
    body: (ownerEmail: string | null) =>
      `Please contact the owner of this Knowledge Graph endpoint to manage its plan and billing${
        ownerEmail ? `: ${ownerEmail}` : '.'
      }`,
    buttonText: 'Close',
  },
  home: {
    title: 'Welcome to Stardog Cloud',
    cta: (
      <p>
        Have questions or feedback? Join the&nbsp;
        <ExternalLink href="https://community.stardog.com">
          Stardog Community
        </ExternalLink>
        &nbsp;today.
      </p>
    ),
    new: {
      title: "What's new at Stardog?",
    },
    newUser: {
      title: 'Welcome to Stardog',
      cta: (
        <>
          <p>
            Have questions or feedback? Join the&nbsp;
            <ExternalLink href="https://community.stardog.com/" icon={null}>
              Stardog Community&nbsp;
            </ExternalLink>
            today.
          </p>
        </>
      ),
      intro:
        'Get started using your own data or one of our industry solution packs to experience Voicebox:',
      endpoint: {
        title: 'Manage Endpoints',
        selectPlan: 'New Stardog Cloud Endpoint',
        connectExisting: 'Existing Stardog Endpoint',
      },
      kits: {
        featured: [
          'stardog:c360pack:1.0',
          'stardog:supplychain-demo:1.0',
          'default:insurance:1.0',
        ],
        useOwnData: {
          id: '__use-own-data__',
          name: 'Bring your own data',
          description:
            'Build a conversational data and AI platform against your own data! Once built you can ask questions across your dataset.',
          link: '/get-started/essentials',
        },
      },
    },
  },
  dashboard: {
    quickActionsTitle: 'Quick Actions',
    quickActions: {
      manageEndpointUsers: 'Manage endpoint users',
      manageEndpoints: 'Manage endpoints',
      manageDatabricks: 'Manage Databricks connections',
    },
  },
  internalDashboard: {
    home: {
      title: 'Stardog Employee Home',
      description: 'Access to internal views and reports.',
      disclaimer:
        'Please be mindful about sharing information contained in these views. The views are restricted to Stardog employees intentionally.',
      version: {
        label: 'Stardog Cloud Portal Version:',
        loading: 'Loading version...',
        error: 'Version unavailable',
      },
      actionGrid: {
        userSearch: {
          title: 'Stardog Cloud User Search',
          description: 'Search and lookup details for any Stardog Cloud user.',
        },
        cloudReport: {
          title: 'Cloud Report',
          description:
            'Report containing information about cloud instances waiting to be allocated, total instances allocated, and inactive instances.',
          sdu: {
            columnHeader: 'Lifetime SDUs',
            actionsHeader: 'Actions',
            loading: 'Loading lifetime SDUs',
            lifetimeValue: (value: string) => `Lifetime SDUs: ${value}`,
            unavailable: 'Usage unavailable',
            error: 'Unable to load usage',
            download: 'Download SDU report',
            downloadFailed: (name: string) =>
              `Failed to download Stardog Unit Usage for "${name}".`,
          },
          validation: {
            columnHeader: 'Validation',
            columnTooltip:
              'Flags clouds whose connection linkage looks wrong (missing, wrong type, wrong owner, or ambiguous).',
            warningAriaLabel: 'Connection validation issues',
          },
        },
      },
    },
    userSearch: {
      title: 'Stardog Cloud User Search',
      download: 'Download',
      searchInput: {
        placeholder: 'Search by email, first name, last name, or company',
      },
      filters: {
        staff: {
          label: 'Administrative Staff',
          checked: 'User is Administrative Staff',
          unchecked: 'User is not Administrative Staff',
          indeterminate: 'No filter applied',
        },
        voiceboxApi: {
          label: 'Voicebox API',
          checked: 'User has Voicebox API enabled',
          unchecked: 'User has Voicebox API disabled',
          indeterminate: 'No filter applied',
        },
      },
    },
    userDashboard: {
      error: {
        message: "Unable to find user's information",
        buttonText: 'Return to user search',
      },
      sections: {
        userInfo: {
          detailsKeyNames: {
            email: 'Email',
            firstName: 'First Name',
            lastName: 'Last Name',
            company: 'Company',
            title: 'Title',
            phone: 'Phone',
            useCase: 'Use Case',
            dateJoined: 'Date Joined',
            lastLogin: 'Last Login',
            hasStardogFree: 'Has Stardog Free',
            isDatabricksUser: 'Databricks User',
            isDesignerStorageEnabled: 'Designer Storage',
            isVerified: 'Verified',
            isStaff: 'Administrative Staff',
            isVoiceboxAPIEnabled: 'Voicebox API',
            isVoiceboxFourEnabled: 'Voicebox 4',
            isCollaborationEnabled: '[DEV ONLY] Designer Collaboration',
          },
          userFeatures: {
            voiceboxAPI: {
              enabled: 'Enabled Voicebox API',
              disabled: 'Disabled Voicebox API',
            },
            voiceboxFour: {
              enabled: 'Enabled Voicebox 4',
              disabled: 'Disabled Voicebox 4',
            },
            collaboration: {
              enabled: 'Enabled Designer Collaboration',
              disabled: 'Disabled Designer Collaboration',
            },
          },
        },
        connections: {
          header: 'Connections',
        },
        clouds: {
          header: 'Cloud Instances',
          description: 'Select a row to get additional information.',
        },
        archivedClouds: {
          header: 'Archived Clouds',
        },
        selectedCloudDetails: {
          grafanaButtonText: 'View Instance in Grafana',
          detailsKeyNames: {
            endpoint: 'Endpoint',
            flavor: 'Flavor',
            status: 'Status',
            region: 'Region',
            paymentRef: 'Payment Ref',
            created: 'Created',
            updated: 'Updated',
          },
        },
      },
    },
  },
  marketplace: {
    demoKits: [
      'stardog:underwriting-demo:1.0',
      'default:insurance:1.0',
      'stardog:singleprotocol:1.0',
      'stardog:supplychain-demo:1.0',
      'stardog:c360pack:1.0',
      'stardog:training-c360:1.0',
      'stardog:healthcare-demo:1.0',
      'default:flight_planning:1.0',
      'stardog:royals-demo:1.0',
      'stardog:starwars:2.0',
      'stardog:superheroes-demo:1.0',
    ],
    title: 'Knowledge Kits',
    summary:
      'Demos, tutorials, data sets, and data models built to speed up your Knowledge Graph journey.',
    loadMoreButton: 'Load more',
    noKits: 'No Knowledge Kits found',
    voiceboxKits: 'Voicebox enabled',
    sections: {
      demos: {
        title: 'Demos',
        description:
          'Learn Stardog basics by browsing industry-related and pop culture Knowledge Graphs.',
      },
      tutorials: {
        title: 'Tutorials',
        description:
          'Master specific Knowledge Graph concepts, from SPARQL to Machine Learning.',
      },
      dataSetsAndModels: {
        title: 'Data sets and data models',
        description:
          'Build your Knowledge Graph faster using preloaded data sets and data models.',
      },
    },
    install: {
      started: 'Installation Started',
      complete: 'Installation complete!',
      failed: 'Installation failed!',
      createdDatabase: (db: string) => `Created database ${db}`,
      failedToCreateDatabase: (db: string) =>
        `Unable to create database ${db}. ${components.marketplace.install.failed}`,
    },
    menu: {
      install: 'Install',
      search: {
        placeholder: 'Search the kits',
      },
      filter: {
        placeholder: 'Filter results by name',
      },
      tagFilter: {
        loadingPlaceholder: 'Loading tags...',
        placeholder: 'Filter tags...',
      },
    },
    card: {
      menu: {
        open: 'Open',
        install: 'Install',
        delete: 'Uninstall',
      },
    },
    overview: {
      title: 'Overview',
      actions: 'Try It Out',
      about: 'About',
      related: 'Related Kits',
      prov: "What's inside?",
      ctaInstall: 'Install in your endpoint',
      ctaInstallDatabase: 'Database',
      demoExplorerButton: 'Open in Explorer',
      demoStudioButton: 'Open in Studio',
      install: 'Install',
      openInVoicebox: 'Open in Voicebox',
      meta: {
        links: 'Additional Content',
        lastModified: 'Last Modified',
        publisher: 'Publisher',
        license: 'License',
        contributors: 'Contributors',
        size: 'Size',
        tags: 'Tags',
        primary: 'Primary Concept',
        themes: 'Core Concepts',
      },
    },
    kitSelector: {
      openInExplorer: 'Open in Explorer',
      connectionSelectorInitialText: 'Select Endpoint',
      createDatabaseTooltip:
        'Database will be created during module installation.',
      databaseSelectorButton: 'Select or Create Database',
      databaseSelectorPlaceholder: 'Search or create...',
    },
    validation: {
      databaseName: {
        required: 'Database name is required',
        characters: (chars: string[]) =>
          `The database name is invalid. The ${
            chars.length > 1 ? 'characters' : 'character'
          } "${chars.join('", "')}" ${
            chars.length > 1 ? 'are' : 'is'
          } not allowed.`,
      },
    },
  },
  databrickspartner: {
    welcome: `Get Started with Databricks Partner Connect`,
    databricksConnectionLabel: 'Databricks Connection Name',
    searchConnections: 'Search Connections',
    cta: (
      username: string | undefined,
      workspaceUrl: string,
      workspaceId: string
    ) => (
      <p>
        Hi {username}! You have come here from Databricks Partner Connect
        Workspace&nbsp;
        <ExternalLink href={workspaceUrl}>{workspaceId}</ExternalLink>
        &nbsp;.
      </p>
    ),
    removeConnectionFailMessage: (detailError: string | undefined) =>
      `Failed to remove connection - ${detailError}`,
    connectionMenu: {
      create: 'Create Datasource',
      edit: 'Edit Datasource',
      delete: 'Delete Datasource',
      deletePartnerConnection: 'Delete Partner Connection',
      deletePrompt: 'Are you sure you want to delete this connection?',
    },
    update: 'Update Datasource',
    dialogTitle: 'Manage Databricks Datasource',
    header: 'Databricks Partner Connections',
    gettingStartedHeader: 'Getting Started',
    information: (
      <p>
        You don&apos;t have any Databricks Partner Connection. Please visit
        the&nbsp;
        <ExternalLink href="https://databricks.com/partnerconnect">
          Databricks Partner Connect
        </ExternalLink>
        &nbsp; portal to get yourself connected.
      </p>
    ),
    uninstall: {
      started: 'Removing Databricks Source',
      complete: 'Databricks data source Removed!',
      failed: 'Databricks data source & starter kit uninstallation failed!',
    },
    stardogServer: 'Stardog Server',
    deletePermanentlyConfirmation:
      'You will lose all the details of Partner Connection. Do you wish to continue?',
    deletePermanently: 'Delete Permanently',
    stardogInstanceGettingAllocated:
      'Please wait till Stardog Instance is getting allocated',
    workspaceInfo: (email: string | undefined) => (
      <p>
        The administrator of the Stardog instance for this Databricks workspace
        is&nbsp;
        <a href={'mailto:'.concat(email || '')}>{email}</a>. Please contact the
        administrator to continue setting up your Stardog account
      </p>
    ),
    getStardogInstance: (
      <p>
        <Icon icon={IconNames.INFO_SIGN} /> You do not have Stardog Connection
        configured. <strong>Get a Stardog Cloud instance</strong> by clicking
        the button above.
      </p>
    ),
    useActionsToManageConnection: (
      <p>
        <Icon icon={IconNames.INFO_SIGN} /> Use <strong>Actions</strong> to
        manage Databricks datasource and get connected with your workspace!
      </p>
    ),
    datasource: {
      creation: {
        started: 'Databricks Datasource creation started',
        completed:
          'Databricks Datasource creation completed. You can click on Stardog server connection to interact with Databricks on Stardog Studio or Designer.',
      },
      deletion: {
        completed: 'Databricks Datasource successfully deleted',
        virtualGraphInfo: (virtualGraphs: string[]) =>
          'Deleting the data source will delete the following virtual graph(s): \n'.concat(
            virtualGraphs.join(', ')
          ),
      },
    },
    starterkit: {
      install: {
        complete: 'Databricks starter kit installation complete',
        failed: 'Databricks starter kit installation failed',
      },
    },
  },
  copyToClipboardButton: {
    initialText: 'Copy',
    successText: 'Copied!',
    failureText: 'Failed to Copy',
  },
  endpoints: {
    endpoint: {
      isAllocating: 'Stardog Cloud instance is being built',
      isExpired: 'Your checkout session has expired!',
      isWaitingForPayment: 'Please complete payment',
    },
    inputs: {
      cancel: {
        label: "Please type 'CANCEL' to confirm.",
      },
      delete: {
        label: "Please type 'DELETE' to confirm.",
      },
      endpoint: {
        label: 'Endpoint URL',
        help: '(URL of your existing Knowledge Graph Endpoint)',
        validationRequired: 'Endpoint URL is required',
        validationHttp: 'Endpoint URL must start with "http"',
      },
      internalEndpoint: {
        label: 'Internal Endpoint',
        help:
          'Optional internal endpoint for server-to-server communication in private networks',
        placeholder: 'http://stardog-internal:5820',
      },
      name: {
        label: 'Name',
        help: '(A unique name to identify this Knowledge Graph endpoint)',
        validationRequired: 'Name is required',
      },
      isDefault: {
        label: 'Grant access to all members:',
      },
      email: {
        label: 'Email',
        validation: (values: string[]) =>
          `Invalid email${values.length === 1 ? '' : 's'}: ${values.join(
            ', '
          )}`,
      },
      roles: {
        defaultLabel: 'Default roles for all members',
        shareLabel: 'Assign roles',
        create: (role: string) => `Use "${role}"`,
      },
      useSSO: {
        label: 'Connect using SSO credentials:',
      },
      ssoScope: {
        label: 'Authorization scope',
        help:
          'The scope registered at your identity provider for this endpoint. The Stardog Cloud Portal requests this scope during token exchange to authenticate users.',
        placeholder: 'e.g., stardog-prod or api://stardog/.default',
        validationRequired: 'Authorization scope is required',
      },
      username: {
        label: 'Username',
        validationRequired: 'Username is required when password is provided',
      },
      password: {
        label: 'Password',
        validationRequired: 'Password is required',
        validationRequiredWithUsername:
          'Password is required when username is provided',
      },
      ssoRegistration: {
        label: 'SSO Connection Registration',
        help:
          "If you don't see your preconfigured SSO connection in the list, please check with your system administrator to ensure SSO connections are set up for your organization.",
        validationRequired: 'Connection Registration is required',
      },
    },
    addEndpointDialog: {
      title: 'Add Endpoint',
      buttons: {
        cancel: 'Cancel',
        submit: 'Save',
      },
    },
    addSSOEndpointDialog: {
      title: 'Add SSO Endpoint',
      description: {
        overview: 'Please fill out the details to add a new SSO endpoint.',
        redirect:
          'After adding the endpoint, you will be redirected to your SSO provider to authenticate.',
      },
      buttons: {
        cancel: 'Cancel',
        submit: 'Save',
      },
    },
    editEndpointDialog: {
      title: 'Edit Endpoint',
      buttons: {
        cancel: 'Cancel',
        submit: 'Save',
      },
    },
    deleteEndpointDialog: {
      title: 'Delete Endpoint',
      description: {
        pre: 'Are you sure you want to delete',
        post: (endpoint: string) => `${endpoint}?`,
      },
      buttons: {
        cancel: 'Cancel',
        submit: 'Delete',
      },
      notifications: {
        success: 'Deleted endpoint.',
        error: 'Failed to delete endpoint.',
      },
    },
    cancelEndpointDialog: {
      title: 'Cancel Cloud',
      buttons: {
        cancel: 'Cancel',
        submit: 'I understand the consequences, cancel my cloud',
      },
      warning: {
        pre: 'Cloud cancellation ',
        strong: 'cannot',
        post: (endpoint: string) =>
          ` be undone. This will permanently delete your Stardog Cloud endpoint (${endpoint}).`,
      },
      notifications: {
        success: 'Cancelled cloud endpoint.',
        error: 'Failed to cancel cloud endpoint.',
      },
    },
    addMyOrgEndpointDialog: {
      title: 'Connect to Stardog',
      buttons: {
        cancel: 'Cancel',
        submit: 'Connect',
      },
    },
    editMyOrgEndpointDialog: {
      title: 'Edit Endpoint',
      buttons: {
        cancel: 'Cancel',
        submit: 'Save',
      },
    },
    signInDialog: {
      title: 'Sign In',
      header: 'Sign in to',
      buttons: {
        submit: 'Connect',
      },
      ssoRequired:
        'This deployment requires SSO connections. Re-create this endpoint using SSO to sign in again.',
    },
    shareOrgEndpointDialog: {
      title: 'Add User',
      buttons: {
        cancel: 'Cancel',
        submit: 'Invite',
      },
      description: 'Grant access to this Knowledge Graph endpoint.',
      notifications: {
        success: 'Added users to endpoint.',
        error: 'Adding User',
      },
    },
    editSharedOrgEndpointDialog: {
      title: 'Edit Roles',
      buttons: {
        cancel: 'Cancel',
        submit: 'Save',
      },
      description: 'Grant access to this Knowledge Graph endpoint.',
      notifications: {
        success: 'Updated user roles.',
        error: 'Updating Role',
      },
    },
    renameMySharedEndpointDialog: {
      title: 'Rename Endpoint',
      buttons: {
        cancel: 'Cancel',
        submit: 'Save',
      },
    },
    shareEndpointDialog: {
      title: 'Add User',
      buttons: {
        cancel: 'Cancel',
        submit: 'Invite',
      },
      description:
        'Invite others to this Knowledge Graph endpoint. New users will be prompted to create an account.',
      notifications: {
        success: 'Invited users to endpoint.',
        error: 'Inviting User',
      },
    },
    editSharedEndpointDialog: {
      title: 'Edit Roles',
      buttons: {
        cancel: 'Cancel',
        submit: 'Save',
      },
      description:
        'Assign one or more roles to control what users can access and manage.',
      notifications: {
        success: 'Updated roles.',
        error: 'Updating Role',
      },
    },
    dashboard: {
      title: 'Endpoints',
      breadcrumb: {
        endpoints: 'Endpoints',
      },
      addExisting: 'Connect to an Existing Endpoint',
      addCloud: 'Create new Knowledge Graph Endpoint',
      addSso: 'Connect to an SSO Endpoint',
    },
    endpointTable: {
      title: (count: number) => `Endpoints (${count})`,
      search: 'Search',
      columns: {
        name: 'Name',
        url: 'URL',
        owner: 'Owner',
        auth: 'Authentication',
        action: 'Action',
      },
      row: {
        auth: {
          browser: 'Browser Auth',
          sso: 'SSO',
          orgSSO: 'Organization SSO',
          username: (username: string) => `Username: ${username}`,
          ssoRequiredWarning: {
            label: 'This connection does not use SSO',
            tooltip:
              'This launchpad deployment requires SSO connections. Recreate this connection using SSO.',
          },
        },
      },
      actions: {
        editEndpoint: 'Edit Endpoint',
        renameSharedEndpoint: 'Rename Endpoint',
        manageUsers: 'Manage Users',
        manageBilling: 'Manage Billing',
        viewDiagnostics: 'View Diagnostic Report',
        downloadLogs: 'Download Logs',
        downloadLogsPermissionsTooltip: (name: string) =>
          `You do not have the required permission to download logs for "${name}". Please contact your Stardog administrator.`,
        downloadVoiceboxReport: 'Download Voicebox Report',
        downloadVoiceboxReportPermissionsTooltip: (name: string) =>
          `You do not have the required permissions to download the Voicebox Report for "${name}". Please contact your Stardog administrator.`,
        downloadSdu: 'Download Stardog Unit Usage',
        downloadSduPermissionsTooltip: (name: string) =>
          `You do not have the required permissions to download Stardog Unit Usage for "${name}". Please contact your Stardog administrator.`,
        cancelCloud: 'Cancel Cloud',
        deleteEndpoint: 'Delete Endpoint',
        deleteSharedEndpoint: 'Leave Endpoint',
      },
      notifications: {
        deleteSharedEndpoint: {
          success: 'Left endpoint.',
          error: 'Failed to leave endpoint.',
        },
      },
      confirmations: {
        deleteSharedEndpoint: {
          title: 'Leave Endpoint',
          message: (name: string) =>
            `Are you sure you want to leave the endpoint ${name}?`,
          confirm: 'Leave',
          cancel: 'Cancel',
        },
      },
    },
    userTable: {
      title: (count: number) => `Users (${count})`,
      search: 'Search',
      add: 'Invite User',
      columns: {
        name: 'Name',
        role: 'Role',
        status: 'Status',
        action: 'Action',
      },
      status: {
        owner: 'Owner',
        active: 'Active',
        invited: 'Invited',
        expired: 'Invitation Expired',
      },
      actions: {
        editRoles: 'Edit Roles',
        removeUser: 'Remove User',
        resendInvitation: 'Resend Invitation',
        removeInvitation: 'Remove Invitation',
      },
      notifications: {
        removeUser: {
          success: 'Removed user.',
          error: 'Removing User',
        },
        resendInvitation: {
          success: 'Invitation sent.',
          error: 'Failed to resend invitation.',
        },
        removeInvitation: {
          success: 'Removed invitation.',
          error: 'Failed to remove invitation.',
        },
      },
      confirmations: {
        removeUser: {
          title: 'Remove User',
          message: (name: string) =>
            `Are you sure you want to remove ${name} from this endpoint?`,
          confirm: 'Remove',
          cancel: 'Cancel',
        },
        removeInvitation: {
          title: 'Remove Invitation',
          message: (email: string) =>
            `Are you sure you want to remove the invitation for ${email}?`,
          confirm: 'Remove',
          cancel: 'Cancel',
        },
      },
    },
  },
  organization: {
    dialog: {
      inputs: {
        delete: {
          label: 'Please type DELETE to confirm.',
        },
        name: {
          label: 'Organization Name',
          footer: 'This organization belongs to a business or organization',
          validationRequired: 'Name is required',
        },
        description: {
          label: 'Description',
        },
        owner: {
          label: 'Organization Owner',
        },
        admin: {
          label: 'Organization Admins',
        },
        email: {
          create: (values: string[]) =>
            `Invite ${values.map((value) => `"${value}"`).join(', ')}`,
          validation: (values: string[]) =>
            `Invalid email${values.length === 1 ? '' : 's'}: ${values.join(
              ', '
            )}`,
        },
        domain: {
          label: 'Domain Name',
          help: (domain: string) =>
            `Your URL will be: https://cloud.stardog.com/org/${domain}`,
          validationRequired: 'Domain is required',
          validationStart: 'Domain must start with a lowercase letter ([a-z])',
          validationChars:
            'Domain can only contain lowercase letters, numbers, and hyphens ([a-z0-9-])',
          validationHyphens:
            'Domain can only use single hyphens between segments',
        },
      },
    },
    addDialog: {
      title: 'Add Organization',
      buttons: {
        cancel: 'Cancel',
        submit: 'Save',
      },
    },
    deleteDialog: {
      title: 'Delete Organization',
      buttons: {
        cancel: 'Cancel',
        submit: 'I understand the consequences, delete organization',
      },
      warning: {
        pre: 'Organization deletion ',
        strong: 'cannot',
        post:
          ' be undone. This action will permanently delete your organization information, including any owned Stardog instances.',
      },
      notifications: {
        success: 'Deleted organization.',
        error: 'Failed to delete organization.',
      },
    },
    editDialog: {
      title: 'Edit Organization',
      buttons: {
        cancel: 'Cancel',
        submit: 'Save',
      },
      notifications: {
        success: 'Edited organization.',
      },
    },
    transferDialog: {
      title: 'Transfer Ownership',
      buttons: {
        cancel: 'Cancel',
        submit: 'Save',
      },
      notifications: {
        success: 'Transferred organization ownership.',
        error: 'Failed to transfer organization ownership.',
      },
    },
    leaveDialog: {
      title: 'Leave Organization',
      buttons: {
        cancel: 'Cancel',
        submitLeave: 'Leave',
        submitTransferAndLeave: 'Transfer & Leave',
      },
      help: {
        owner: 'Select new organization owner',
        member: 'Are you sure you want to leave this organization?',
      },
      warning: {
        line_1: 'You are currently the owner of this organization.',
        line_2:
          'Before you leave, please transfer ownership to another member.',
        line_3:
          'The new owner will gain full administrative control over the organization.',
      },
      notifications: {
        success: 'Left organization.',
        error: 'Failed to leave organization.',
      },
    },
    inviteDialog: {
      title: 'Invite Member',
      steps: {
        selectMembers: 'Select Members',
        selectEndpoints: 'Select Endpoints',
      },
      buttons: {
        back: 'Back',
        cancel: 'Cancel',
        next: 'Next',
        submit: 'Invite',
      },
      selectMembers: {
        email: {
          label: 'Emails',
          validationRequired: 'Email is required',
          validationExists: (values: string[]) =>
            `Existing member${values.length === 1 ? '' : 's'}: ${values.join(
              ', '
            )}`,
        },
        role: {
          label: 'Access Level',
          admin: 'Organization Admin',
          member: 'Organization Member',
        },
      },
      selectEndpoints: {
        endpoints: {
          label: 'Endpoint Access',
          select: 'Select Endpoints',
          default: (count: number) => `Default Endpoints (${count})`,
        },
        table: {
          title: 'Endpoints',
          search: 'Search',
          viewAll: 'View All',
          columns: {
            name: 'Endpoint Name',
            endpoint: 'URL',
            role: 'Roles',
          },
          role: {
            create: (role: string) => `Use "${role}"`,
          },
        },
      },
      notifications: {
        success: 'Sent membership invitations.',
      },
    },
    dashboard: {
      breadcrumb: {
        endpoints: 'Endpoints',
        units: 'Stardog Units',
      },
      cards: {
        members: {
          title: 'Members',
          subtitle: 'Total Members',
        },
        endpoints: {
          title: 'Endpoints',
          subtitle: 'Organization Endpoints',
        },
        units: {
          title: 'Stardog Units',
          subtitle: 'Total Consumption',
        },
      },
      memberVisibility: {
        title: 'Member visibility is restricted',
        body:
          'Members cannot see the other members of this organization. The member list is only visible to admins and owners.',
      },
      deleteOrganization: 'Delete Organization',
      editOrganization: 'Edit Organization',
      inviteMember: 'Invite Member',
      configureSso: 'Configure SSO',
      leaveOrganization: 'Leave Organization',
      transferOrganization: 'Transfer Ownership',
    },
    endpointTable: {
      title: (count: number) => `Endpoints (${count})`,
      search: 'Search',
      add: 'Connect to an Existing Endpoint',
      columns: {
        name: 'Name',
        url: 'URL',
        auth: 'Authentication',
        action: 'Action',
      },
    },
    memberTable: {
      title: (count: number) => `Members (${count})`,
      search: 'Search',
      columns: {
        name: 'Name',
        role: 'Role',
        status: 'Status',
        actions: 'Action',
      },
      roles: {
        OWNER: 'Owner',
        ADMIN: 'Admin',
        MEMBER: 'Member',
      },
      status: {
        active: 'Active',
        invited: 'Invited',
        expired: 'Invitation Expired',
      },
      actions: {
        removeMember: 'Remove Member',
        resendInvitation: 'Resend Invitation',
        removeInvitation: 'Remove Invitation',
      },
      notifications: {
        removeMember: {
          success: 'Removed member.',
          error: 'Failed to remove member.',
        },
        resendInvitation: {
          success: 'Invitation sent.',
          error: 'Failed to resend invitation.',
        },
        removeInvitation: {
          success: 'Removed invitation.',
          error: 'Failed to remove invitation.',
        },
      },
      confirmations: {
        removeMember: {
          title: 'Remove Member',
          message: (name: string) =>
            `Are you sure you want to remove ${name} from this organization?`,
          confirm: 'Remove',
          cancel: 'Cancel',
        },
        removeInvitation: {
          title: 'Remove Invitation',
          message: (email: string) =>
            `Are you sure you want to remove the invitation for ${email}?`,
          confirm: 'Remove',
          cancel: 'Cancel',
        },
      },
    },
    userTable: {
      title: (count: number) => `Users (${count})`,
      search: 'Search',
      add: 'Add User',
      columns: {
        name: 'Name',
        role: 'Role',
        action: 'Action',
      },
      actions: {
        editRoles: 'Edit Roles',
        removeUser: 'Remove User',
      },
      notifications: {
        removeUser: {
          success: 'Removed user.',
          error: 'Removing User',
        },
      },
      confirmations: {
        removeUser: {
          title: 'Remove User',
          message: (name: string) =>
            `Are you sure you want to remove ${name} from this endpoint?`,
          confirm: 'Remove',
          cancel: 'Cancel',
        },
      },
    },
  },
  configureOrgSSO: {
    dialog: {
      providers: {
        okta: 'Okta',
        microsoftEntra: 'Microsoft Entra ID',
      },
      secretInput: {
        cancel: 'Cancel',
        configured: 'Secret Configured',
        help: 'Leave blank to keep the existing secret.',
        replace: 'Replace',
      },
      entra: {
        discoveryUrl: {
          label: 'Discovery URL',
          placeholder:
            'https://login.microsoftonline.com/{tenant-id}/v2.0/.well-known/openid-configuration',
          helpCreate: 'Found in your App Registration under Endpoints.',
          helpEdit:
            'Changing this will require all members to verify their email the next time they sign in.',
        },
        clientId: {
          label: 'Client ID',
          help:
            'Found on the Overview page of your App Registration in the Azure portal.',
        },
        clientSecret: {
          label: 'Client Secret',
        },
        idpLogout: {
          label: 'Enable IDP Logout',
          help: 'Log users out of Entra when they log out of Stardog Cloud',
        },
      },
      okta: {
        webApp: {
          heading: 'Web Application',
          description:
            'Your Okta application that members will use to sign in to Stardog Cloud.',
          discoveryUrl: {
            label: 'Discovery URL',
            placeholder:
              'https://your-domain.okta.com/.well-known/openid-configuration',
            helpCreate:
              'Found in your Okta Admin Console under your Custom Authorization Server.',
            helpEdit:
              'Changing this will require all members to verify their email the next time they sign in.',
          },
          audience: {
            label: 'Authorization Server Audience',
            help:
              'The Audience value from your Custom Authorization Server settings.',
          },
          clientId: {
            label: 'Client ID',
            help:
              'Found on the General tab of your Okta Web Application (Authorization Code grant type).',
          },
          clientSecret: {
            label: 'Client Secret',
          },
        },
        apiServicesApp: {
          heading: 'API Services Application',
          description:
            'A separate Okta application configured with the Token Exchange grant type, used to securely exchange user tokens for Stardog access.',
          clientId: {
            label: 'API Services Client ID',
          },
          clientSecret: {
            label: 'API Services Client Secret',
          },
        },

        idpLogout: {
          label: 'Enable IDP Logout',
          help: 'Log users out of Okta when they log out of Stardog Cloud',
        },
      },
      view: {
        provider: {
          label: 'Provider',
        },
        loginUrl: {
          label: 'Login URL',
          help: (domain: string) =>
            `Share this URL with your team to sign in via SSO. Members can also enter "${domain}" on the login page.`,
        },
        idpConfig: {
          label: 'Identity Provider Configuration',
          help:
            "Add these URLs to your identity provider's allowed redirect URIs.",
        },
        redirectUri: {
          label: 'Redirect URI',
        },
        postLogoutRedirectUri: {
          label: 'Post-Logout Redirect URI',
        },
        delete: {
          cancel: 'Cancel',
          submit: 'Yes, Delete',
          warning: {
            line_1: 'Are you sure you want to delete this SSO configuration?',
            line_2:
              'Members will no longer be able to sign in with SSO and will need to verify their email again if SSO is reconfigured.',
          },
        },
      },
    },
    selectProvider: {
      title: 'Configure SSO',
      label: 'Choose an identity provider',
      buttons: {
        cancel: 'Cancel',
        submit: 'Continue',
      },
    },
    create: {
      title: (provider: string) => `Configure SSO - ${provider}`,
      buttons: {
        cancel: 'Back',
        submit: 'Save Configuration',
      },
    },
    view: {
      title: 'SSO Configuration',
      buttons: {
        delete: 'Delete Configuration',
        edit: 'Edit Configuration',
        submit: 'Done',
      },
    },
    edit: {
      title: (provider: string) => `Edit SSO Configuration - ${provider}`,
      buttons: {
        cancel: 'Cancel',
        submit: 'Save',
      },
    },
  },
  voicebox: {
    chatListAside: {
      filter: {
        placeholder: 'Search',
      },
      noResults: 'No results.',
      title: 'Chat list',
    },
    downloadCsvError: /* istanbul ignore next */ (err: string) =>
      `Could not download results: ${err}`,
    downloadCsvSuccess: /* istanbul ignore next */ (filename: string) =>
      `Downloaded results to ${filename}`,
    sidebar: {
      filter: {
        placeholder: 'Filter',
      },
      new: 'New Chat',
      noResults: 'No results.',
      rename: 'Rename',
      delete: {
        delete: 'Delete',
        message: 'Are you sure you want to delete this conversation?',
        title: 'Delete Conversation',
        cancel: 'Cancel',
      },
    },
  },
  launchpad: {
    login: {
      dialogTitle: 'Login to Stardog Launchpad',
    },
    dashboard: {
      title: 'Stardog Launchpad',
      welcome: 'Welcome to Stardog',
    },
    emptyState: {
      title: 'No Endpoints',
      description: 'You have not connected any endpoints.',
    },
    connections: {
      addBasic: 'Add an Endpoint',
      addSso: 'Add SSO Endpoint',
    },
    connectionDetails: {
      info: {
        username: 'Username',
        endpoint: 'Endpoint',
        alivenessCheck: 'Aliveness Check',
      },
      tags: {
        sso: 'SSO',
      },
      buttons: {
        reauthenticate: 'Re-authenticate',
        logout: 'Logout',
        edit: 'Edit Endpoint',
        delete: 'Delete Endpoint',
        diagnostics: 'Diagnostics',
      },
      errorCallouts: {
        basicAuthFailure: {
          title: 'Stardog Authentication Failed',
          body: /* istanbul ignore next  */ (
            reauthorizationButtoon: JSX.Element
          ) => {
            return (
              <div>
                <p>
                  Authentication to the Stardog endpoint has failed. Please try
                  re-authenticating.
                </p>
                {reauthorizationButtoon}
              </div>
            );
          },
        },
        browserAuthFailure: {
          title: 'Stardog Authentication Failed',
          body:
            'Browser authentication failed for this endpoint. Please re-authenticate.',
        },
        ssoAuthFailure: {
          noToken: {
            title: 'SSO Authentication Required',
            body: /* istanbul ignore next  */ (
              reauthorizionButton: JSX.Element
            ) => {
              return (
                <div>
                  <p>
                    Your session has expired. Please log in again to continue
                    using this Stardog endpoint. This endpoint requires Single
                    Sign-On (SSO) authentication.
                  </p>
                  {reauthorizionButton}
                </div>
              );
            },
          },
          existingToken: {
            title: 'Stardog Authentication Failed',
            body: /* istanbul ignore next  */ (
              reauthorizionButton: JSX.Element
            ) => {
              return (
                <>
                  <p>
                    Your authentication for this endpoint has expired or is not
                    valid.
                  </p>
                  <p>
                    This could be due to an expired token, Stardog endpoint
                    configuration issues, or SSO provider settings. You can try
                    re-authenticating or contact your system administrator if
                    the issue persists.
                  </p>
                  {reauthorizionButton}
                </>
              );
            },
          },
        },
      },
    },
  },
};

export const landingDocumentationList = [
  {
    name: 'Getting Started with Stardog Cloud',
    hyperlink:
      'https://docs.stardog.com/get-started/getting-started-stardog-cloud',
    description: '',
  },
  {
    name: 'Virtual Graphs',
    hyperlink: 'https://docs.stardog.com/virtual-graphs/',
    description: '',
  },
  {
    name: 'Inference Engine',
    hyperlink: 'https://docs.stardog.com/inference-engine/',
    description: '',
  },
  {
    name: 'Stardog Designer',
    hyperlink: 'https://docs.stardog.com/stardog-applications/designer/',
    description: '',
  },
  {
    name: 'Stardog Explorer',
    hyperlink: 'https://docs.stardog.com/stardog-applications/explorer/',
    description: '',
  },
];

export const landingToolsList = [
  {
    name: 'Tutorials',
    hyperlink: '/kits?type=urn%3Astardog%3Amodules%3Atypes%3ATutorial',
    internal: true,
  },
  {
    name: 'Demos',
    hyperlink: '/kits?type=urn%3Astardog%3Amodules%3Atypes%3ADemo',
    internal: true,
  },
  {
    name: 'More Kits...',
    hyperlink: '/kits',
    description: 'Load your database with a selection of pre-made datasets',
    internal: true,
  },
];

export const landingTutorialsList = [
  {
    name: 'Getting Started Series',
    hyperlink: 'https://docs.stardog.com/tutorials/getting-started-series/',
    description: '',
  },
  {
    name: 'Using Virtual Graphs',
    hyperlink: 'https://docs.stardog.com/tutorials/using-virtual-graphs',
    description: '',
  },
  {
    name: 'Virtual Graph Mappings',
    hyperlink: 'https://docs.stardog.com/tutorials/virtual-graph-mappings',
    description: '',
  },
  {
    name: 'Learn SPARQL',
    hyperlink: 'https://docs.stardog.com/tutorials/learn-sparql',
    description: '',
  },
  {
    name: 'Build a Python App',
    hyperlink: 'https://docs.stardog.com/tutorials/build-a-python-app',
    description: '',
  },
];

export const databricksExtBlogList = [
  {
    name: 'Stardog Is Now Available in Databricks Partner Connect',
    hyperlink:
      'https://www.stardog.com/blog/stardog-is-now-available-in-databricks-partner-connect/',
    description: 'Aug 24, 2022, 6 minute read',
  },
];

export const landingBlogList = [
  {
    name:
      'Databricks Lakehouse + Stardog Enterprise Knowledge Graph = Data Democratization',
    hyperlink:
      'https://www.stardog.com/blog/databricks-lakehouse-stardog-enterprise-knowledge-graph-data-democratization/',
    description: 'Mar 22, 2022, 5 minute read',
  },
  {
    name: 'Knowledge Graphs and Machine Learning',
    hyperlink:
      'https://www.stardog.com/blog/knowledge-graphs-and-machine-learning/',
    description: 'Mar 17, 2022, 10 minute read',
  },
  {
    name: 'How to Build a Knowledge Graph',
    hyperlink: 'https://www.stardog.com/building-a-knowledge-graph/',
    description: 'Feb 23, 2022, 13 minute read',
  },
];
